import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    ShowButton,
    Show,
    SimpleShowLayout,
    FunctionField,
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    SelectArrayInput,
    EditButton,
    Toolbar,
    SaveButton,
    TopToolbar,
    ListButton,
    ExportButton,
    Filter,
    SearchInput,
    useUpdate,
    useNotify,
    useRecordContext,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Alert, FormControl, InputLabel, Select, MenuItem, type SelectChangeEvent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { QRCodeSVG } from 'qrcode.react';
import QRScanner from '../components/QRScanner';

const getAppUrl = () => {
    const origin = window.location.origin;
    if (origin.includes('localhost')) {
        return 'http://localhost:3000';
    }
    if (origin.includes('admin.neverknock.com')) {
        return 'https://app.neverknock.com';
    }
    return origin.replace('/admin', '');
};

const getApiUrl = () => {
    const origin = window.location.origin;
    if (origin.includes('localhost')) {
        return process.env.REACT_APP_API_URL || 'http://localhost:8000';
    }
    return 'https://api.neverknock.com';
};

interface HOAOption {
    uuid: string;
    name: string;
}

const useHoaOptions = (open: boolean) => {
    const [hoas, setHoas] = React.useState<HOAOption[]>([]);
    React.useEffect(() => {
        if (!open) return;
        const token = localStorage.getItem('admin_token');
        fetch(`${getApiUrl()}/api/v1/hoas/?skip=0&limit=100`, {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        })
            .then((r) => r.json())
            .then((data) => {
                const items = Array.isArray(data) ? data : data.items || [];
                setHoas(items.map((h: any) => ({ uuid: h.uuid, name: h.name })));
            })
            .catch(() => {});
    }, [open]);
    return hoas;
};

const GenerateSignDialog = () => {
    const [open, setOpen] = React.useState(false);
    const [generatedUuid, setGeneratedUuid] = React.useState<string | null>(null);
    const [copied, setCopied] = React.useState(false);
    const [generating, setGenerating] = React.useState(false);
    const [genError, setGenError] = React.useState<string | null>(null);
    const [selectedHoa, setSelectedHoa] = React.useState('');
    const qrRef = React.useRef<SVGSVGElement>(null);
    const qrCodeUrl = generatedUuid ? `${getAppUrl()}/offer/${generatedUuid}` : '';
    const hoas = useHoaOptions(open);

    const handleGenerateUuid = async () => {
        const uuid = crypto.randomUUID();
        setGenerating(true);
        setGenError(null);
        try {
            const token = localStorage.getItem('admin_token');
            const body: any = { uuid };
            if (selectedHoa) body.hoa_uuid = selectedHoa;
            const response = await fetch(`${getApiUrl()}/api/v1/admin/generated-signs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Failed to register sign');
            }
            setGeneratedUuid(uuid);
            setCopied(false);
        } catch (err: any) {
            setGenError(err.message || 'Failed to generate sign');
        } finally {
            setGenerating(false);
        }
    };

    const handleCopy = async () => {
        if (generatedUuid) {
            await navigator.clipboard.writeText(generatedUuid);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleDownloadSvg = () => {
        const svg = qrRef.current;
        if (!svg) return;
        const clone = svg.cloneNode(true) as SVGSVGElement;
        // Remove the background path so only QR code modules remain
        // (3D print slicers like Bambu Studio interpret all paths as geometry)
        clone.querySelectorAll('path').forEach((path) => {
            if (path.getAttribute('fill') !== '#000000') path.remove();
        });
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(clone);
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qr-${generatedUuid}.svg`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <Button onClick={() => setOpen(true)} startIcon={<AddIcon />} variant="contained">
                Generate New Sign
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Generate New Sign</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Create a new home qr code not yet assigned to a user or home.
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }} size="small">
                        <InputLabel>HOA (optional)</InputLabel>
                        <Select
                            value={selectedHoa}
                            label="HOA (optional)"
                            onChange={(e) => setSelectedHoa(e.target.value)}
                        >
                            <MenuItem value="">None</MenuItem>
                            {hoas.map((h) => (
                                <MenuItem key={h.uuid} value={h.uuid}>{h.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={handleGenerateUuid} disabled={generating} sx={{ mb: 2 }}>
                        {generating ? 'Generating...' : 'Generate'}
                    </Button>
                    {genError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{genError}</Alert>
                    )}

                    {generatedUuid && (
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                bgcolor: 'background.default',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            <Typography variant="body1" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                                {generatedUuid}
                            </Typography>
                            <Button variant="contained" onClick={handleCopy} startIcon={<ContentCopyIcon />} size="small">
                                {copied ? 'Copied!' : 'Copy'}
                            </Button>
                        </Box>
                    )}

                    {generatedUuid && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <QRCodeSVG ref={qrRef} value={qrCodeUrl} size={220} level="M" bgColor="transparent" />
                            <Typography
                                variant="caption"
                                sx={{ maxWidth: 420, textAlign: 'center', color: 'text.secondary', wordBreak: 'break-all' }}
                            >
                                {qrCodeUrl}
                            </Typography>
                            <Button variant="outlined" onClick={handleDownloadSvg} startIcon={<DownloadIcon />}>
                                Download QR SVG
                            </Button>
                        </Box>
                    )}

                    {copied && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            UUID copied to clipboard!
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpen(false); setSelectedHoa(''); setGeneratedUuid(null); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const AddTrustedSignDialog = () => {
    const [open, setOpen] = React.useState(false);
    const [scannerOpen, setScannerOpen] = React.useState(false);
    const [lastResult, setLastResult] = React.useState<{ uuid: string; status: 'success' | 'duplicate' | 'error'; message: string } | null>(null);
    const [scannedCount, setScannedCount] = React.useState(0);
    const [isRegistering, setIsRegistering] = React.useState(false);
    const [selectedHoa, setSelectedHoa] = React.useState('');
    const hoas = useHoaOptions(open);

    const handleScanSuccess = async (uuid: string) => {
        setScannerOpen(false);
        setIsRegistering(true);
        try {
            const token = localStorage.getItem('admin_token');
            const body: any = { uuid };
            if (selectedHoa) body.hoa_uuid = selectedHoa;
            const response = await fetch(`${getApiUrl()}/api/v1/admin/generated-signs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });
            if (response.ok) {
                setLastResult({ uuid, status: 'success', message: 'Sign registered successfully' });
                setScannedCount((c) => c + 1);
            } else if (response.status === 409) {
                setLastResult({ uuid, status: 'duplicate', message: 'This sign is already registered' });
            } else {
                const data = await response.json();
                setLastResult({ uuid, status: 'error', message: data.detail || 'Failed to register sign' });
            }
        } catch (err: any) {
            setLastResult({ uuid, status: 'error', message: err.message || 'Network error' });
        } finally {
            setIsRegistering(false);
        }
    };

    const handleScanAnother = () => {
        setLastResult(null);
        setScannerOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setScannerOpen(false);
        setLastResult(null);
        setSelectedHoa('');
    };

    return (
        <>
            <Button onClick={() => { setOpen(true); setScannerOpen(true); }} startIcon={<QrCodeScannerIcon />} variant="outlined">
                Add Trusted Sign
            </Button>

            <QRScanner
                open={scannerOpen}
                onScanSuccess={handleScanSuccess}
                onClose={() => setScannerOpen(false)}
                extraContent={
                    <FormControl fullWidth size="small">
                        <InputLabel>HOA (optional)</InputLabel>
                        <Select
                            value={selectedHoa}
                            label="HOA (optional)"
                            onChange={(e: SelectChangeEvent) => setSelectedHoa(e.target.value)}
                            MenuProps={{ style: { zIndex: 2000 } }}
                        >
                            <MenuItem value="">None</MenuItem>
                            {hoas.map((h) => (
                                <MenuItem key={h.uuid} value={h.uuid}>{h.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                }
            />

            <Dialog open={open && !scannerOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Add Trusted Sign</DialogTitle>
                <DialogContent>
                    {isRegistering && (
                        <Typography sx={{ py: 2, textAlign: 'center' }}>Registering sign...</Typography>
                    )}

                    {lastResult && !isRegistering && (
                        <Box sx={{ py: 1 }}>
                            <Alert
                                severity={lastResult.status === 'success' ? 'success' : lastResult.status === 'duplicate' ? 'info' : 'error'}
                                sx={{ mb: 2 }}
                            >
                                {lastResult.message}
                            </Alert>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 2 }}>
                                UUID: {lastResult.uuid}
                            </Typography>
                            {scannedCount > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {scannedCount} sign{scannedCount !== 1 ? 's' : ''} added this session
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Done</Button>
                    <Button onClick={handleScanAnother} variant="contained" startIcon={<QrCodeScannerIcon />}>
                        Scan Another
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const HomeQrCodeButton: React.FC<{ record: any }> = ({ record }) => {
    const [open, setOpen] = React.useState(false);
    const qrRef = React.useRef<SVGSVGElement>(null);

    if (!record?.user_uuid || !record?.home_uuid) return null;

    const qrCodeUrl = `${getAppUrl()}/offer/${record.home_uuid}`;

    const handleDownloadSvg = () => {
        const svg = qrRef.current;
        if (!svg) return;
        const clone = svg.cloneNode(true) as SVGSVGElement;
        // Match GenerateSignDialog: keep only the QR module paths so 3D-print
        // slicers don't pick up the background as geometry.
        clone.querySelectorAll('path').forEach((path) => {
            if (path.getAttribute('fill') !== '#000000') path.remove();
        });
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(clone);
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qr-${record.home_uuid}.svg`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Dialog renders into a Portal, but React still bubbles synthetic events
    // through the component tree — so a click inside the dialog can trigger
    // the datagrid row click. The wrapper here swallows those events.
    const stop = (e: React.MouseEvent | React.SyntheticEvent) => e.stopPropagation();

    return (
        <Box component="span" onClick={stop} onMouseDown={stop}>
            <Button
                size="small"
                variant="outlined"
                startIcon={<QrCodeScannerIcon />}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                }}
            >
                QR
            </Button>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                onClick={stop}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Home QR Code</DialogTitle>
                <DialogContent>
                    {record.home_address && (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            {record.home_address}
                        </Typography>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <QRCodeSVG ref={qrRef} value={qrCodeUrl} size={220} level="M" bgColor="transparent" />
                        <Typography
                            variant="caption"
                            sx={{ maxWidth: 420, textAlign: 'center', color: 'text.secondary', wordBreak: 'break-all' }}
                        >
                            {qrCodeUrl}
                        </Typography>
                        <Button variant="outlined" onClick={handleDownloadSvg} startIcon={<DownloadIcon />}>
                            Download QR SVG
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const ORDER_STATUS_OPTIONS = [
    { id: 'pending', name: 'Pending' },
    { id: 'paid', name: 'Paid' },
    { id: 'shipped', name: 'Shipped' },
    { id: 'delivered', name: 'Delivered' },
    { id: 'cancelled', name: 'Cancelled' },
];

const InlineStatusField = () => {
    const record = useRecordContext<any>();
    const notify = useNotify();
    const [update, { isPending }] = useUpdate();
    const [value, setValue] = React.useState<string>(record?.order_status || '');

    React.useEffect(() => {
        setValue(record?.order_status || '');
    }, [record?.order_status]);

    if (!record) return null;

    const stop = (e: React.MouseEvent | React.SyntheticEvent) => e.stopPropagation();

    const handleChange = (e: SelectChangeEvent) => {
        const next = e.target.value;
        const previous = value;
        setValue(next);
        update(
            'sign_orders',
            {
                id: record.id,
                data: { order_status: next },
                previousData: record,
            },
            {
                onSuccess: () => {
                    notify('Status updated', { type: 'success' });
                },
                onError: (err: any) => {
                    setValue(previous);
                    notify(err?.message || 'Failed to update status', { type: 'error' });
                },
            }
        );
    };

    return (
        <FormControl size="small" sx={{ minWidth: 130 }} onClick={stop} onMouseDown={stop}>
            <Select
                value={value}
                onChange={handleChange}
                disabled={isPending}
                variant="outlined"
                sx={{ fontSize: '0.875rem' }}
                MenuProps={{ onClick: stop }}
            >
                {ORDER_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id} onClick={stop}>
                        {opt.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const ExportRowCsvButton = () => {
    const record = useRecordContext<any>();
    if (!record) return null;

    const handleExport = (e: React.MouseEvent) => {
        e.stopPropagation();
        const keys = Object.keys(record).sort();
        const escape = (v: any) => {
            if (v === null || v === undefined) return '';
            const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
            return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = `${keys.join(',')}\n${keys.map((k) => escape(record[k])).join(',')}\n`;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sign-order-${record.id || 'export'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Button size="small" onClick={handleExport} startIcon={<DownloadIcon />}>
            CSV
        </Button>
    );
};

const SignOrderListActions = () => (
    <TopToolbar>
        <ExportButton />
        <AddTrustedSignDialog />
        <GenerateSignDialog />
    </TopToolbar>
);

const SignOrderFilter = (props: any) => (
    <Filter {...props}>
        <SearchInput
            source="search"
            placeholder="Search address, email, or name"
            alwaysOn
            sx={{
                width: 400,
                '& .MuiInputBase-root': { width: '100%' },
            }}
        />
        <SelectArrayInput
            source="order_status"
            label="Status"
            choices={ORDER_STATUS_OPTIONS}
            alwaysOn
            sx={{ minWidth: 200 }}
        />
        <SelectArrayInput
            source="exclude_order_status"
            label="Exclude status"
            choices={ORDER_STATUS_OPTIONS}
            alwaysOn
            sx={{ minWidth: 200 }}
        />
    </Filter>
);

export const SignOrderList = () => (
    <List
        sort={{ field: 'updated_at', order: 'DESC' }}
        actions={<SignOrderListActions />}
        filters={<SignOrderFilter />}
    >
        <Datagrid>
            <TextField source="id" label="Order ID" />
            <TextField source="user_name" label="Customer Name" />
            <TextField source="user_email" label="User Email" />
            <FunctionField
                label="Signup"
                render={(record: any) =>
                    record.user_uuid ? (
                        <Chip label="Authenticated" size="small" color="primary" variant="outlined" />
                    ) : (
                        <Chip label="Express" size="small" color="warning" variant="outlined" />
                    )
                }
            />
            <FunctionField
                label="Home QR"
                render={(record: any) => <HomeQrCodeButton record={record} />}
            />
            <TextField source="home_address" label="Home Address" />
            <FunctionField
                label="Sign Color"
                render={(record: any) => record.sign_color ? record.sign_color.charAt(0).toUpperCase() + record.sign_color.slice(1) : ''} 
            />
            <FunctionField
                label="Status"
                render={() => <InlineStatusField />}
            />
            <FunctionField 
                label="Tracking" 
                render={(record: any) => {
                    if (!record.tracking_number) {
                        return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
                    }
                    if (record.tracking_url) {
                        return (
                            <a 
                                href={record.tracking_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'underline' }}
                            >
                                {record.tracking_number}
                            </a>
                        );
                    }
                    return record.tracking_number;
                }} 
            />
            <DateField source="first_scanned_at" label="First Scanned" sortable={true} showTime />
            <DateField source="created_at" label="Created" sortable={true} />
            <DateField source="updated_at" label="Updated" sortable={true} />
            <ExportRowCsvButton />
            <EditButton />
            <ShowButton />
        </Datagrid>
    </List>
);

const SignOrderEditToolbar = () => {
    const navigate = useNavigate();
    
    return (
        <Toolbar>
            <SaveButton />
            <Button
                onClick={() => navigate('/sign_orders')}
                sx={{ marginLeft: 2 }}
            >
                Cancel
            </Button>
        </Toolbar>
    );
};

export const SignOrderEdit = () => (
    <Edit>
        <SimpleForm toolbar={<SignOrderEditToolbar />}>
            <TextField source="id" label="Order ID" />
            <TextField source="user_email" label="User Email" />
            <TextField source="home_address" label="Home Address" />
            <TextField source="sign_color" label="Sign Color" />
            
            <SelectInput 
                source="order_status" 
                label="Order Status"
                choices={[
                    { id: 'pending', name: 'Pending' },
                    { id: 'paid', name: 'Paid' },
                    { id: 'shipped', name: 'Shipped' },
                    { id: 'delivered', name: 'Delivered' },
                    { id: 'cancelled', name: 'Cancelled' },
                ]}
            />
            
            <TextInput source="tracking_number" label="Tracking Number" fullWidth />
            <TextInput source="carrier" label="Carrier" />
            <TextInput source="tracking_url" label="Tracking URL" fullWidth />
            <TextInput source="shipping_label_url" label="Shipping Label URL" fullWidth />
        </SimpleForm>
    </Edit>
);

const SignOrderShowActions = () => (
    <TopToolbar sx={{ justifyContent: 'flex-start' }}>
        <ListButton label="Back to list" />
    </TopToolbar>
);

export const SignOrderShow = () => (
    <Show actions={<SignOrderShowActions />}>
        <SimpleShowLayout>
            <TextField source="id" label="Order ID" />
            <TextField source="user_name" label="Customer Name" />
            <TextField source="user_email" label="User Email" />
            <FunctionField
                label="Signup"
                render={(record: any) =>
                    record.user_uuid ? (
                        <Chip label="Authenticated" size="small" color="primary" variant="outlined" />
                    ) : (
                        <Chip label="Express" size="small" color="warning" variant="outlined" />
                    )
                }
            />
            <FunctionField
                label="Home QR"
                render={(record: any) =>
                    record.user_uuid && record.home_uuid ? (
                        <HomeQrCodeButton record={record} />
                    ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>No home linked</span>
                    )
                }
            />
            <TextField source="home_address" label="Home Address" />
            <FunctionField 
                label="Sign Color" 
                render={(record: any) => record.sign_color ? record.sign_color.charAt(0).toUpperCase() + record.sign_color.slice(1) : '-'} 
            />
            <FunctionField 
                label="Order Status" 
                render={(record: any) => record.order_status ? record.order_status.charAt(0).toUpperCase() + record.order_status.slice(1) : '-'} 
            />
            <FunctionField 
                label="Stripe Session ID" 
                render={(record: any) => record.stripe_session_id || <span style={{ color: '#999', fontStyle: 'italic' }}>Not available</span>} 
            />
            <FunctionField 
                label="Payment Intent ID" 
                render={(record: any) => record.stripe_payment_intent_id || <span style={{ color: '#999', fontStyle: 'italic' }}>Not available</span>} 
            />
            <FunctionField 
                label="Tracking Number" 
                render={(record: any) => record.tracking_number || <span style={{ color: '#999', fontStyle: 'italic' }}>Not set</span>} 
            />
            <FunctionField 
                label="Carrier" 
                render={(record: any) => record.carrier || <span style={{ color: '#999', fontStyle: 'italic' }}>Not set</span>} 
            />
            <FunctionField 
                label="Tracking URL" 
                render={(record: any) => record.tracking_url || <span style={{ color: '#999', fontStyle: 'italic' }}>Not set</span>} 
            />
            <DateField source="created_at" label="Created At" />
            <DateField source="updated_at" label="Updated At" />
            <FunctionField
                label="First Scanned At"
                render={(record: any) => record.first_scanned_at ? new Date(record.first_scanned_at).toLocaleString() : <span style={{ color: '#999', fontStyle: 'italic' }}>Not scanned</span>}
            />
            <FunctionField
                label="Paid At"
                render={(record: any) => record.paid_at ? new Date(record.paid_at).toLocaleString() : <span style={{ color: '#999', fontStyle: 'italic' }}>Not paid</span>} 
            />
            <FunctionField 
                label="Shipped At" 
                render={(record: any) => record.shipped_at ? new Date(record.shipped_at).toLocaleString() : <span style={{ color: '#999', fontStyle: 'italic' }}>Not shipped</span>} 
            />
            <FunctionField 
                label="Delivered At" 
                render={(record: any) => record.delivered_at ? new Date(record.delivered_at).toLocaleString() : <span style={{ color: '#999', fontStyle: 'italic' }}>Not delivered</span>} 
            />
        </SimpleShowLayout>
    </Show>
);
